<template>
    <form-validation
        v-if="currentStep === FormStep.SelectBrand"
        v-slot="{ bindings: validationBindings }"
    >
        <flex-container>
            <flex-container direction="column" :gap="Size.xs">
                <text-heading>
                    Aanmaken
                </text-heading>
                <text-description>
                    <span v-text="t('intro')" />
                </text-description>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container direction="column" :gap="Size.sm">
                <input-group v-model="form.brand" :schema="object().required()">
                    <template #label>
                        <text-heading>
                            <span v-text="t('chooseBrand')" />
                        </text-heading>
                    </template>
                    <template #input>
                        <brand-select-input />
                    </template>
                </input-group>

                <!-- Add website button-->
                <div v-if="hasAskNoaBranding()">
                    <button-text @click="goToStep(FormStep.CreateWebsite)">
                        <svg-icon
                            :icon="PlusIcon"
                            :size="IconSize.xs"
                            is-custom-icon
                        />
                        <text-decorator :size="FontSize.xs">
                            Website toevoegen
                        </text-decorator>
                    </button-text>
                </div>
            </flex-container>

            <!-- Actions -->
            <flex-container align-items="center" justify-content="flex-end">
                <slot name="actions" :bindings="{ resetForm }" />

                <button-block
                    :disabled="!validationBindings.isValid.value"
                    @loading="goToStep(FormStep.SelectRole, $event)"
                >
                    Volgende
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>

    <form-validation
        v-if="currentStep === FormStep.SelectRole"
        v-slot="{ bindings: validationBindings }"
    >
        <flex-container>
            <flex-container no-wrap justify-content="space-between">
                <flex-container
                    :size="5"
                    direction="column"
                    :gap="Size.xs"
                >
                    <text-heading>
                        Aanmaken
                    </text-heading>
                    <text-description>
                        Wat is de missie van je A.I. medewerker?<br>
                        Kies of je wilt focussen op klantenservice, het genereren van leads, of beide.
                    </text-description>
                </flex-container>

                <div class="create-assistent-svg">
                    <target-svg
                        :style="{ color: getCssColor('default', 'primary') }"
                    />
                </div>
            </flex-container>

            <horizontal-line no-margin />

            <input-group
                v-model="form.role"
                :schema="object().required()"
            >
                <template #label>
                    <text-heading>
                        Bepaal het doel
                    </text-heading>
                </template>
                <template #input>
                    <role-icon-radio-group-input />
                </template>
            </input-group>

            <!-- Actions -->
            <flex-container align-items="center" justify-content="flex-end">
                <slot name="actions" :bindings="{ resetForm }" />

                <button-outline @click="goToStep(FormStep.SelectBrand)">
                    Vorige
                </button-outline>

                <template v-if="hasWinckBranding()">
                    <button-block
                        :disabled="!validationBindings.isValid.value"
                        @loading="goToStep(FormStep.SelectLeads, $event)"
                    >
                        Volgende
                    </button-block>
                </template>
                <template v-else>
                    <button-block
                        :disabled="!validationBindings.isValid.value"
                        @loading="goToStep(FormStep.DefinePersonality, $event)"
                    >
                        Volgende
                    </button-block>
                </template>
            </flex-container>
        </flex-container>
    </form-validation>

    <form-validation
        v-if="currentStep === FormStep.SelectLeads"
        v-slot="{ bindings: validationBindings }"
    >
        <flex-container>
            <flex-container no-wrap justify-content="space-between">
                <flex-container
                    :size="5"
                    direction="column"
                    :gap="Size.xs"
                >
                    <text-heading>
                        Aanmaken
                    </text-heading>
                    <text-description>
                        Kies voor de leadgeneratie wat voor soort leads de A.I. medewerker moet vergaren.<br>
                        Meerdere soorten leads zijn hierbij mogelijk om te selecteren.
                    </text-description>
                </flex-container>

                <div class="create-assistent-svg">
                    <target-svg
                        :style="{ color: getCssColor('default', 'primary') }"
                    />
                </div>
            </flex-container>

            <horizontal-line no-margin />

            <input-group
                :model-value="form.leadGoals"
                :schema="array().min(1).required()"
            >
                <template #label>
                    <text-heading>
                        Wat voor soort leads wil je vergaren?
                    </text-heading>
                </template>
                <template #input>
                    <goals-checkbox-group-input />
                </template>
            </input-group>

            <flex-container align-items="center" justify-content="flex-end">
                <slot name="actions" :bindings="{ resetForm }" />

                <button-outline @click="goToStep(FormStep.SelectRole)">
                    Vorige
                </button-outline>

                <button-block
                    :disabled="!validationBindings.isValid.value"
                    @loading="goToStep(FormStep.DefinePersonality, $event)"
                >
                    Volgende
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>

    <form-validation
        v-if="currentStep === FormStep.DefinePersonality"
        v-slot="{ bindings: validationBindings }"
    >
        <flex-container>
            <flex-container no-wrap justify-content="space-between">
                <flex-container direction="column" :gap="Size.xs">
                    <text-heading>
                        Persoonlijkheid
                    </text-heading>
                    <text-description>
                        Kies de persoonlijkheid en communicatiestijl van je A.I. Medewerker.
                    </text-description>
                </flex-container>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container>
                <flex-container :screen-sizes="{ desktop: 5 }">
                    <input-group v-model="form.name" :schema="string().required()">
                        <template #label>
                            <input-label>
                                Naam van de A.I. Medewerker
                            </input-label>
                        </template>
                        <template #input>
                            <text-input placeholder="Noa" />
                        </template>
                    </input-group>
                </flex-container>

                <flex-container :screen-sizes="{ desktop: 5 }">
                    <input-group v-model="form.personality" :schema="Personality.validationSchema.required()">
                        <template #label>
                            <input-label>
                                Persoonlijkheid
                            </input-label>
                        </template>
                        <template #input>
                            <select-input :items="Personality.values" />
                        </template>
                    </input-group>
                </flex-container>

                <flex-container :screen-sizes="{ desktop: 5 }">
                    <input-group v-model="form.formality" :schema="Formality.validationSchema.required()">
                        <template #label>
                            <input-label>
                                Spreekvorm
                            </input-label>
                        </template>
                        <template #input>
                            <radio-group-input :items="Formality.values">
                                <template #default="{ bindings }">
                                    <text-decorator :size="FontSize.xs">
                                        {{ bindings.item.label }}
                                    </text-decorator>
                                </template>
                            </radio-group-input>
                        </template>
                    </input-group>
                </flex-container>

                <flex-container :screen-sizes="{ desktop: 5 }">
                    <input-group v-model="form.answerLength" :schema="AnswerLength.validationSchema.required()">
                        <template #label>
                            <input-label>
                                Antwoordlengte
                            </input-label>
                        </template>
                        <template #input>
                            <radio-group-input
                                :items="AnswerLength.values"
                                label-key-name="label"
                                value-key-name="value"
                            >
                                <template #default="{ bindings }">
                                    <flex-container
                                        v-tooltip="bindings.item.tooltip"
                                        :gap="Size.xs"
                                        align-items="center"
                                        inline
                                        no-wrap
                                    >
                                        <text-decorator :size="FontSize.xs">
                                            {{ bindings.item.label }}
                                        </text-decorator>
                                        <svg-icon
                                            :color="'gray-500'"
                                            :icon="IconCircleInfo"
                                            :size="IconSize.xxs"
                                            is-custom-icon
                                        />
                                    </flex-container>
                                </template>
                            </radio-group-input>
                        </template>
                    </input-group>
                </flex-container>

                <flex-container :screen-sizes="{ desktop: 5 }">
                    <input-group v-model="form.allowEmoticons">
                        <template #label>
                            <input-label>
                                Gebruik van emoticon's / emoji's
                            </input-label>
                        </template>
                        <template #input>
                            <switch-input />
                            <text-decorator :size="FontSize.xs">
                                {{ form.allowEmoticons ? 'Toestaan' : 'Uitgeschakeld' }}
                            </text-decorator>
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container align-items="center" justify-content="flex-end">
                <slot name="actions" :bindings="{ resetForm }" />

                <template v-if="hasWinckBranding()">
                    <button-outline @click="goToStep(FormStep.SelectLeads)">
                        Vorige
                    </button-outline>
                </template>
                <template v-else>
                    <button-outline @click="goToStep(FormStep.SelectRole)">
                        Vorige
                    </button-outline>
                </template>

                <button-block
                    :disabled="!validationBindings.isValid.value"
                    @loading="createAssistent($event)"
                >
                    Aanmaken
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>

    <form-validation
        v-if="currentStep === FormStep.CreateWebsite"
        v-slot="{ bindings: validationBindings }"
    >
        <flex-container>
            <flex-container no-wrap justify-content="space-between">
                <flex-container
                    :size="5"
                    direction="column"
                    :gap="Size.xs"
                >
                    <text-heading>
                        Ingevoerde URL
                    </text-heading>
                    <text-description>
                        Voeg een nieuwe website url toe om een extra asssitent toe te voegen.
                    </text-description>
                </flex-container>

                <div class="create-assistent-svg">
                    <building-website-svg
                        :style="{ color: getCssColor('default', 'primary') }"
                    />
                </div>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container direction="column" :gap="Size.sm">
                <text-heading>
                    Website url
                </text-heading>
                <input-group v-model="form.websiteUrl" :schema="string().url().required()">
                    <template #input>
                        <text-input placeholder="https://" />
                    </template>
                </input-group>
            </flex-container>

            <!-- Actions -->
            <flex-container align-items="center" justify-content="flex-end">
                <slot name="actions" :bindings="{ resetForm }" />
                <button-outline @click="goToStep(FormStep.SelectBrand)">
                    Vorige
                </button-outline>
                <button-block
                    :disabled="!validationBindings.isValid.value"
                    @loading="createBrand"
                >
                    Aanmaken
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import TargetSvg from '@/assets/icons/undraw/target.svg?component';
    import BuildingWebsiteSvg from '@/assets/icons/undraw/building_websites.svg?component';
    import {
        ButtonState,
        type ButtonStateChangedEvent,
        FlexContainer,
        FontSize,
        FormValidation,
        getCssColor,
        IconSize,
        InputGroup,
        RadioGroupInput,
        Size,
        SvgIcon,
        TextDecorator,
    } from '@keyvue/one-ui';
    import BrandSelectInput from '@/company/brand/components/input/BrandSelectInput.vue';
    import RoleIconRadioGroupInput, { type AssistentRoleOption } from '@/ai/assistent/components/input/RoleIconRadioGroupInput.vue';
    import GoalsCheckboxGroupInput from '@/ai/assistent/components/input/GoalsCheckboxGroupInput.vue';
    import { AnswerLength, type Brand, Formality, Personality } from '@keyvue/one-api-client';
    import { array, object, string } from 'yup';
    import PlusIcon from '@keyvue/one-awesome-icons/icons/regular/plus.svg?component';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { useI18n } from 'vue-i18n';
    import messages from './CreateAssistentForm.messages.json';
    import IconCircleInfo from '@keyvue/one-awesome-icons/icons/regular/circle-info.svg?component';

    export interface Form {
        brand: Brand | null
        role: AssistentRoleOption | null,
        leadGoals: any[],
        websiteUrl: string | null,
        name: string,
        personality: Personality,
        formality: Formality,
        answerLength: AnswerLength,
        allowEmoticons: boolean,
    }

    export interface ExposedCreateAssistentForm {
        form: Form,
        setForm: (values: Form) => void,
        resetForm: () => void,
    }

    enum FormStep {
        SelectBrand,
        SelectRole,
        SelectLeads,
        CreateWebsite,
        DefinePersonality,
    }

    const emit = defineEmits<{
        (e: 'create-assistent', event: ButtonStateChangedEvent): void;
        (e: 'create-brand', event: ButtonStateChangedEvent): void;
    }>();

    const currentStep = ref<FormStep>(FormStep.SelectBrand);
    const form = ref<Form>({
        brand: null,
        role: null,
        leadGoals: [],
        websiteUrl: null,
        name: 'Noa',
        personality: Personality.CASUAL,
        formality: Formality.INFORMAL,
        answerLength: AnswerLength.BALANCED,
        allowEmoticons: true,
    });

    const { t } = useI18n({ messages });
    const { hasWinckBranding, hasAskNoaBranding } = useAppBranding();

    function goToStep(formStep: FormStep, event?: ButtonStateChangedEvent): void {
        currentStep.value = formStep;

        if (event !== undefined) {
            event.button.setState(ButtonState.content);
        }
    }

    function setForm(values: Form) {
        form.value = values;
    }

    function resetForm() {
        goToStep(FormStep.SelectBrand);
        setForm({
            brand: null,
            role: null,
            leadGoals: [],
            websiteUrl: null,
            name: 'Noa',
            personality: Personality.CASUAL,
            formality: Formality.INFORMAL,
            answerLength: AnswerLength.BALANCED,
            allowEmoticons: true,
        });
    }

    function createAssistent(event: ButtonStateChangedEvent) {
        emit('create-assistent', event);
    }

    function createBrand(event: ButtonStateChangedEvent) {
        emit('create-brand', event);
    }

    defineExpose({
        form,
        setForm,
        resetForm,
    });
</script>

<style scoped lang="scss">
.create-assistent-svg {
    width: 40%;
}
</style>
